import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Languages, Quote } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { translations } from "@/i18n/translations";
import { useLanguage } from "@/context/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const BioBlock = ({ data }) => (
  <div className="space-y-4">
    {data.paragraphs.map((p) => (
      <p key={p.slice(0, 40)} className="text-sm leading-relaxed text-[var(--muted-ink)] sm:text-[15px]">{p}</p>
    ))}
  </div>
);

export const About = () => {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState(lang);

  useEffect(() => { setTab(lang); }, [lang]);

  return (
    <section id="about" className="relative overflow-hidden bg-white py-16 lg:py-24">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
        {/* Portrait */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease }}
          className="relative"
        >
          <div className="absolute -inset-3 -z-10 rounded-[26px] bg-[image:var(--bg-facet)]" />
          <div className="overflow-hidden rounded-[22px] border border-[color:var(--border-subtle)] bg-white shadow-[var(--shadow-float)]">
            <img src="/assets/angelina.jpg" alt="Ms. A Vidal" className="aspect-[3/3.6] w-full object-cover object-top" loading="lazy" />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2.5">
            <div className="flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] bg-[var(--surface-2)] px-3.5 py-3">
              <BadgeCheck className="h-5 w-5 text-[var(--success)]" />
              <span className="text-sm font-semibold text-[var(--brand-charcoal)]">{t.about.verified}</span>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] bg-[var(--surface-2)] px-3.5 py-3">
              <Languages className="h-5 w-5 text-[var(--brand-burgundy)]" />
              <span className="text-sm font-semibold text-[var(--brand-charcoal)]">{t.about.languages}</span>
            </div>
          </div>
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, ease, delay: 0.08 }}
        >
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--brand-burgundy)]">{t.about.eyebrow}</span>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[var(--brand-navy)] sm:text-4xl">
            {t.about.header}
          </h2>
          <p className="mt-1 text-base font-medium text-[var(--brand-burgundy)]">{t.about.subheader}</p>

          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <TabsList className="rounded-full bg-[var(--surface-2)] p-1" data-testid="about-bio-tabs">
              <TabsTrigger value="en" className="rounded-full px-4 text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--brand-burgundy)] data-[state=active]:shadow-sm" data-testid="about-tab-en">{t.about.tabsEn}</TabsTrigger>
              <TabsTrigger value="es" className="rounded-full px-4 text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--brand-burgundy)] data-[state=active]:shadow-sm" data-testid="about-tab-es">{t.about.tabsEs}</TabsTrigger>
            </TabsList>
            <TabsContent value="en" className="mt-5"><BioBlock data={translations.en.about} /></TabsContent>
            <TabsContent value="es" className="mt-5"><BioBlock data={translations.es.about} /></TabsContent>
          </Tabs>

          <div className="mt-7 flex items-start gap-3 rounded-2xl border-l-4 border-l-[var(--brand-burgundy)] bg-[var(--surface-2)] p-5">
            <Quote className="h-7 w-7 shrink-0 text-[var(--brand-burgundy)]" />
            <p className="font-heading text-xl font-semibold italic text-[var(--brand-navy)] sm:text-2xl">
              {t.about.motto}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
